import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../AuthContext';
import { useAdminTheme } from '../ThemeContext';
import {
  LogOut, Menu, Sun, Moon, X, ChevronDown, Settings, Bell, Eye, ExternalLink,
  PanelLeft, HelpCircle, Key,
} from 'lucide-react';
import '../admin-theme.css';

// Layout components extracted into /layout/ during the iter175 refactor.
import { TOP_ITEMS, HUBS, getHubItems } from './layout/hubs';
import { HubRow } from './layout/HubRow';
import { Flyout } from './layout/Flyout';
import { NewButton } from './layout/NewButton';
import { TopbarSearch } from './layout/TopbarSearch';
import { ChangePasswordModal } from './layout/ChangePasswordModal';

export default function AdminLayout({ children }) {
  const { user, logout } = useAdminAuth();
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const { theme, toggle } = useAdminTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expanded, setExpanded] = useState({}); // hubId -> true when expanded

  // Auto-expand the hub that contains the currently active route so the user
  // can see siblings in the sidebar without an extra click.
  useEffect(() => {
    const activeHub = HUBS.find((h) =>
      getHubItems(h).some((it) => location.pathname === it.href || (it.href !== '/admin' && location.pathname.startsWith(it.href + '/')))
    );
    if (activeHub) setExpanded((prev) => ({ ...prev, [activeHub.id]: true }));
  }, [location.pathname]);

  // Telemetry: fire-and-forget POST on every admin route change so we can
  // answer "which sections do admins actually use?" with data instead of guesses.
  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) return;
    const t = localStorage.getItem('admin_token');
    if (!t) return;
    fetch('/api/admin/telemetry/visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
      body: JSON.stringify({ route: location.pathname }),
    }).catch(() => {});
  }, [location.pathname]);
  const [hoverHub, setHoverHub] = useState(null); // for rail-mode flyout
  const [hoverRect, setHoverRect] = useState(null);
  const [stats, setStats] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  // Per-hub "last viewed" timestamps (ISO). When `hub_activity[id]` is newer than
  // `hubLastViewed[id]`, we render an orange "What's new?" dot on that hub tile.
  const [hubLastViewed, setHubLastViewed] = useState(() => {
    if (typeof window === 'undefined') return {};
    try { return JSON.parse(localStorage.getItem('admin_hub_last_viewed') || '{}'); }
    catch { return {}; }
  });

  // Scroll to top on route change
  useEffect(() => {
    const main = document.querySelector('[data-admin-theme]');
    if (main) main.scrollTo(0, 0);
    else window.scrollTo(0, 0);
    setHoverHub(null);
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!token) return;
    const API = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API}/api/admin/auth/permissions`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()).then(d => setPermissions(d.permissions || {})).catch(() => {});
  }, [token]);

  useEffect(() => {
    const t = localStorage.getItem('admin_token');
    if (!t) return;
    const fetchStats = () => fetch('/api/admin/dashboard/stats', { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.ok ? r.json() : null).then(setStats).catch(() => {});
    fetchStats();
    // Refresh stats every 60s so the "What's new?" dots reflect recent activity.
    const id = setInterval(fetchStats, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const hasFreshActivity = (hubId) => {
    const activityIso = stats?.hub_activity?.[hubId];
    if (!activityIso) return false;
    const seenIso = hubLastViewed[hubId];
    if (!seenIso) return true; // never viewed and there is activity
    return activityIso > seenIso;
  };

  const markHubViewed = (hubId) => {
    const now = new Date().toISOString();
    setHubLastViewed((prev) => {
      const next = { ...prev, [hubId]: now };
      try { localStorage.setItem('admin_hub_last_viewed', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const isActive = (href) => {
    if (!href) return false;
    if (href === '/admin') return location.pathname === '/admin';
    if (href === '/admin/blog/new') return location.pathname === '/admin/blog/new' || location.pathname.match(/^\/admin\/blog\/[^/]+$/);
    if (href === '/admin/jobs/new') return location.pathname === '/admin/jobs/new' || location.pathname.match(/^\/admin\/jobs\/[^/]+$/);
    return location.pathname.startsWith(href);
  };
  const isHubActive = (hub) => getHubItems(hub).some(i => isActive(i.href));

  const toggleHub = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const getBadgeCount = (badgeKey) => {
    if (!stats) return null;
    if (badgeKey === 'blog') return stats.blog?.total || null;
    if (badgeKey === 'jobs') return stats.jobs?.active || null;
    if (badgeKey === 'apps') return stats.jobs?.applications || null;
    return null;
  };

  const visibleHubs = HUBS.filter(h => {
    const visibleItems = getHubItems(h).filter(i => !permissions || !i.permission || permissions[i.permission]);
    return visibleItems.length > 0;
  });

  return (
    <div data-admin-theme={theme} className="min-h-screen flex" style={{ background: 'var(--hs-bg-alt)' }}>
      {/* ───────────────── Sidebar ───────────────── */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 ease-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${sidebarCollapsed ? 'w-[72px]' : 'w-[260px]'}`}
        style={{ background: 'var(--hs-sidebar)' }}
      >
        {/* Logo row */}
        <div className="h-[60px] flex items-center justify-between px-5" style={{ borderBottom: '1px solid var(--hs-sidebar-border)' }}>
          {!sidebarCollapsed && (
            <Link to="/admin" className="flex items-center gap-2.5" data-testid="admin-logo">
              <img src="/flynaut-logo-dark.png" alt="Flynaut" className="h-7 w-auto" />
              <div className="flex flex-col">
                <span className="text-[13px] font-bold tracking-tight leading-none" style={{ color: 'var(--hs-sidebar-text)' }}>Flynaut</span>
                <span className="text-[9px] font-semibold tracking-[0.15em] uppercase leading-none mt-0.5" style={{ color: 'var(--hs-sidebar-text-muted)' }}>ADMIN</span>
              </div>
            </Link>
          )}
          <button
            onClick={() => { if (window.innerWidth >= 1024) setSidebarCollapsed(!sidebarCollapsed); else setSidebarOpen(false); }}
            className="p-1.5 rounded-md transition-colors hidden lg:flex"
            style={{ color: 'var(--hs-sidebar-text-muted)' }}
            data-testid="admin-sidebar-toggle"
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hs-sidebar-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <PanelLeft size={16} />
          </button>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden" style={{ color: 'var(--hs-sidebar-text-muted)' }}><X size={18} /></button>
        </div>

        {/* Nav body */}
        <nav className="flex-1 overflow-y-auto py-3 space-y-1" data-testid="admin-nav"
          style={{ paddingLeft: sidebarCollapsed ? '10px' : '12px', paddingRight: sidebarCollapsed ? '10px' : '12px' }}>

          {/* Top-level items (Dashboard) */}
          {TOP_ITEMS.map((item) => {
            if (permissions && item.permission && !permissions[item.permission]) return null;
            const active = isActive(item.href);
            const Icon = item.icon;
            return (
              <Link key={item.href} to={item.href} data-testid={`admin-nav-${item.testId}`}
                className={`flex items-center gap-3 rounded-lg transition-all duration-150`}
                style={{
                  padding: sidebarCollapsed ? '11px' : '10px 12px',
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  background: active ? 'var(--hs-sidebar-active)' : 'transparent',
                  color: active ? 'var(--hs-sidebar-active-text)' : 'var(--hs-sidebar-text-muted)',
                  fontWeight: active ? 700 : 500,
                  fontSize: '13px',
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--hs-sidebar-hover)'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                title={sidebarCollapsed ? item.label : undefined}>
                <Icon size={18} strokeWidth={active ? 2.3 : 1.8} />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--hs-sidebar-border)', margin: '10px 4px' }} />

          {/* Hubs */}
          {visibleHubs.map((hub) => (
            <HubRow
              key={hub.id}
              hub={hub}
              collapsed={sidebarCollapsed}
              expanded={!!expanded[hub.id]}
              active={isHubActive(hub)}
              hasFresh={hasFreshActivity(hub.id)}
              onToggle={() => { toggleHub(hub.id); markHubViewed(hub.id); }}
              onChildClick={() => markHubViewed(hub.id)}
              isActiveFn={isActive}
              getBadgeCount={getBadgeCount}
              permissions={permissions}
              onHoverOpen={(rect) => { setHoverHub(hub); setHoverRect(rect); }}
              onHoverClose={() => setHoverHub((h) => (h?.id === hub.id ? null : h))}
            />
          ))}
        </nav>

        {/* Bottom section */}
        {!sidebarCollapsed && (
          <div className="px-3 py-2" style={{ borderTop: '1px solid var(--hs-sidebar-border)' }}>
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site"
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors text-[13px]"
              style={{ color: 'var(--hs-sidebar-text-muted)' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hs-sidebar-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
              <Eye size={15} />
              <span>View Website</span>
              <ExternalLink size={11} className="ml-auto opacity-40" />
            </a>
          </div>
        )}

        <div className="px-3 py-3" style={{ borderTop: '1px solid var(--hs-sidebar-border)' }}>
          <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${sidebarCollapsed ? 'justify-center' : ''}`}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--hs-accent)' }}>
              <span className="text-white text-[10px] font-bold">{user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2)}</span>
            </div>
            {!sidebarCollapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--hs-sidebar-text)' }}>{user?.name}</p>
                  <p className="text-[10px] truncate capitalize" style={{ color: 'var(--hs-sidebar-text-muted)' }}>{user?.role?.replace('_', ' ')}</p>
                </div>
                <button onClick={() => setShowChangePassword(true)} data-testid="change-password-btn" className="transition-colors hover:text-blue-400 p-1" style={{ color: 'var(--hs-sidebar-text-muted)' }} title="Change Password">
                  <Key size={15} />
                </button>
                <button onClick={logout} data-testid="admin-logout-btn" className="transition-colors hover:text-red-400 p-1" style={{ color: 'var(--hs-sidebar-text-muted)' }} title="Log out">
                  <LogOut size={15} />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Rail-mode flyout popup */}
      {sidebarCollapsed && hoverHub && hoverRect && (
        <Flyout
          hub={hoverHub}
          anchorRect={hoverRect}
          onClose={() => setHoverHub(null)}
          onKeepOpen={() => setHoverHub(hoverHub)}
          isActiveFn={isActive}
          getBadgeCount={getBadgeCount}
          permissions={permissions}
          onItemClick={(href) => { navigate(href); markHubViewed(hoverHub.id); setHoverHub(null); }}
        />
      )}

      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ───────────────── Main ───────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-[52px] flex items-center px-4 lg:px-6 sticky top-0 z-30"
          style={{ background: '#2D3E50', borderBottom: 'none' }} data-testid="admin-topbar">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden mr-3" style={{ color: 'rgba(255,255,255,0.6)' }}><Menu size={18} /></button>
          <Link to="/admin" className="flex items-center gap-2 mr-4 flex-shrink-0" data-testid="topbar-logo">
            <div style={{ width: 28, height: 28, borderRadius: '6px', background: '#F5841F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
            </div>
          </Link>

          <TopbarSearch />

          {/* Right cluster */}
          <div className="flex items-center gap-1 ml-auto">
            <NewButton permissions={permissions} />
            <a href="https://flynaut.notion.site/admin-help" target="_blank" rel="noreferrer" data-testid="topbar-help" className="w-8 h-8 rounded-md flex items-center justify-center transition-colors"
              title="Help & support"
              style={{ color: 'rgba(255,255,255,0.55)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}>
              <HelpCircle size={16} />
            </a>
            <Link to="/admin/security" data-testid="topbar-settings" className="w-8 h-8 rounded-md flex items-center justify-center transition-colors"
              style={{ color: 'rgba(255,255,255,0.55)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}>
              <Settings size={16} />
            </Link>
            <button data-testid="topbar-notifications"
              className="w-8 h-8 rounded-md flex items-center justify-center transition-colors relative"
              style={{ color: 'rgba(255,255,255,0.55)', background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}>
              <Bell size={16} />
              <span style={{ position: 'absolute', top: 2, right: 2, width: 8, height: 8, borderRadius: '50%', background: '#EF4444', border: '1.5px solid #2D3E50' }} />
            </button>
            <button onClick={toggle} data-testid="admin-theme-toggle"
              className="w-8 h-8 rounded-md flex items-center justify-center transition-colors"
              style={{ color: 'rgba(255,255,255,0.55)', background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
              {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
            </button>
            <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.12)', margin: '0 4px' }} />
            <div className="flex items-center gap-2 pl-1 pr-1 py-1 rounded-md cursor-pointer transition-colors" data-testid="topbar-user-profile"
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#F5841F' }}>
                <span className="text-white text-[9px] font-bold">{user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2)}</span>
              </div>
              <span className="text-xs font-medium hidden lg:block" style={{ color: 'rgba(255,255,255,0.8)' }}>Flynaut LLC</span>
              <ChevronDown size={12} style={{ color: 'rgba(255,255,255,0.4)' }} className="hidden lg:block" />
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-8 overflow-y-auto" data-admin-theme={theme}>{children}</main>
      </div>

      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} token={token} />}
    </div>
  );
}

